import * as React from "react";
import { MenuSeparator } from "react-ui";

import { ComboBoxItemContent, ComboBoxItemContentProps } from "./ComboBoxItemContent";
import { AutoValueNote, Item, ItemCode, ItemName } from "./ComboBox.styles";

export interface ComboboxItemProps {
    displayItem?: "value" | "name";
    dataTid: string;
    itemMaxWidth: number | undefined;
    isAutoValueItem?: boolean;
    formula?: React.ReactNode;
    codeContentProps: ComboBoxItemContentProps;
    nameContentProps: ComboBoxItemContentProps;
    greyPicklistValues?: boolean;
}

export function ComboboxItem({
    displayItem,
    dataTid,
    itemMaxWidth,
    isAutoValueItem,
    formula,
    codeContentProps,
    nameContentProps,
    greyPicklistValues,
}: ComboboxItemProps): JSX.Element {
    let itemContent;

    const comboboxNameOnlyItemProps = { ...nameContentProps, greyPicklistValues: greyPicklistValues };
    switch (displayItem) {
        case "value":
            itemContent = <ComboboxCodeOnlyItem {...codeContentProps} />;
            break;
        case "name":
            itemContent = <ComboboxNameOnlyItem {...comboboxNameOnlyItemProps} />;
            break;
        default:
            itemContent = (
                <>
                    <ComboboxCodeOnlyItem {...codeContentProps} />
                    <ComboboxNameOnlyItem {...comboboxNameOnlyItemProps} />
                </>
            );
            break;
    }

    return isAutoValueItem ? (
        <ComboboxAutoValueItem dataTid={dataTid} itemMaxWidth={itemMaxWidth} formula={formula}>
            {itemContent}
        </ComboboxAutoValueItem>
    ) : (
        <Item data-tid={dataTid} maxWidth={itemMaxWidth}>
            {itemContent}
        </Item>
    );
}

const ComboboxCodeOnlyItem = (props: ComboBoxItemContentProps): JSX.Element => (
    <ItemCode>
        <ComboBoxItemContent {...props} />
    </ItemCode>
);

const ComboboxNameOnlyItem = (props: ComboBoxItemContentProps & { greyPicklistValues?: boolean }): JSX.Element => (
    <ItemName isGreyValue={props.greyPicklistValues}>
        <ComboBoxItemContent {...props} />
    </ItemName>
);

const ComboboxAutoValueItem = (props: {
    children: React.ReactNode;
    dataTid: string;
    itemMaxWidth: number | undefined;
    autoValue?: string;
    formula?: React.ReactNode;
}): JSX.Element => {
    const { dataTid, itemMaxWidth, formula, children } = props;
    return (
        <>
            <Item data-tid={`${dataTid}-auto-value`} maxWidth={itemMaxWidth}>
                {children}
                <AutoValueNote data-tid="combobox-auto-value-note">{formula}</AutoValueNote>
            </Item>
            <MenuSeparator data-tid="combobox-auto-value-separator" key="combobox-auto-value-separator" />
        </>
    );
};
